import { type IEmailTo, regexEmail } from "@api";
import { CONFIRM_KEYS } from "@inputs/text/EmailField/constants";
import Autocomplete, { autocompleteClasses } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import type { SxProps, Theme } from "@mui/material/styles";
import { useState } from "react";
import { type Control, Controller } from "react-hook-form";
import type { TEmailForm } from "./resolver";

export interface IEmailsProps {
  label: string;
  control: Control<TEmailForm>;
  emails: readonly IEmailTo[];
  emailsLimit?: number;
  disableEmails?: boolean;
  sx?: SxProps<Theme>;
}

const isValidEmail = (email: string) => regexEmail.test(email);

export const Emails = ({ label, control, emails, emailsLimit, disableEmails, sx }: IEmailsProps) => {
  const [inputValue, setInputValue] = useState("");

  return (
    <Controller
      control={control}
      name="emails"
      render={({ field: { value, onChange, name, ref }, fieldState: { error } }) => (
        <Autocomplete
          fullWidth
          value={value}
          onChange={(_, newValue) => {
            onChange(newValue);
          }}
          options={emails}
          getOptionLabel={(option) => (typeof option === "string" ? option : option.address)}
          isOptionEqualToValue={(option, value) => option.address === value.address}
          inputValue={inputValue}
          onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                name={name}
                label={label}
                inputRef={ref}
                error={Boolean(error)}
                helperText={error?.message}
                size="small"
                sx={sx}
                data-hj-suppress="true"
                slotProps={{

                  htmlInput: {
                    ...params.inputProps,
                    onKeyDown: (event) => {
                      const inputValue = event.currentTarget.value;
                      if (CONFIRM_KEYS.includes(event.key)) {
                        event.preventDefault();
                        const trimmed = inputValue.trim();
                        if (trimmed && isValidEmail(trimmed) && !value.some((v) => v.address === trimmed)) {
                          const newEntry = { address: trimmed, name: trimmed };
                          onChange([...value, newEntry]);
                          setInputValue("");
                        }
                      }
                    },
                  }
                }}
              />
            );
          }}
          limitTags={emailsLimit}
          multiple
          disabled={disableEmails}
          slotProps={{
            chip: {
              color: "yellow",
              size: "small",
              sx: { [`&.${autocompleteClasses.tag}`]: { mt: "1px", mb: 0 } },
            }
          }}
        />
      )}
    />
  );
};
